import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import RichTextEditor from '@/components/RichTextEditor';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Upload, Loader2, Star } from 'lucide-react';
import {
  useImpactRecords,
  useManageImpactRecords,
  ImpactRecord,
  ImpactCategory,
  IMPACT_CATEGORY_LABELS,
  IMPACT_CATEGORY_BADGE,
} from '@/hooks/useImpactRecords';

type FormState = Partial<ImpactRecord>;

const EMPTY: FormState = {
  title: '',
  subtitle: '',
  description: '',
  category: 'conferencia_taller',
  image_url: '',
  video_url: '',
  location: '',
  company: '',
  date: null,
  featured: false,
  sort_order: 0,
};

export default function ImpactManagementPage() {
  const { data: records = [], isLoading } = useImpactRecords();
  const { create, update, remove, uploadImage } = useManageImpactRecords();

  const [filter, setFilter] = useState<'all' | ImpactCategory>('all');
  const [editing, setEditing] = useState<ImpactRecord | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const filtered = useMemo(
    () => (filter === 'all' ? records : records.filter((r) => r.category === filter)),
    [records, filter],
  );

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (r: ImpactRecord) => {
    setEditing(r);
    setForm(r);
    setShowForm(true);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image_url: url }));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.category) return;
    const payload: any = {
      title: form.title,
      subtitle: form.subtitle || null,
      description: form.description || null,
      category: form.category,
      image_url: form.image_url || null,
      video_url: form.video_url || null,
      location: form.location || null,
      company: form.company || null,
      date: form.date || null,
      featured: form.featured ?? false,
      sort_order: form.sort_order ?? 0,
    };
    if (editing) {
      await update.mutateAsync({ id: editing.id, ...payload });
    } else {
      await create.mutateAsync(payload);
    }
    setShowForm(false);
  };

  const toggleFeatured = (r: ImpactRecord) => {
    update.mutate({ id: r.id, featured: !r.featured });
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Gestión de Impacto</h1>
          <p className="text-muted-foreground">
            Administra los registros que se muestran en el home y en la página /impacto.
          </p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo registro
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(['all', 'conferencia_taller', 'proyecto', 'caso_exito', 'testimonio'] as const).map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? 'default' : 'outline'}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'Todos' : IMPACT_CATEGORY_LABELS[f]}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Imagen</TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Empresa / Ubicación</TableHead>
                <TableHead className="w-24">Destacado</TableHead>
                <TableHead className="w-20">Orden</TableHead>
                <TableHead className="w-32 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No hay registros.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {r.image_url ? (
                        <img
                          src={r.image_url}
                          alt=""
                          className="w-12 h-12 rounded object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded bg-muted" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {r.title}
                      {r.subtitle && (
                        <div className="text-xs text-muted-foreground">{r.subtitle}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{IMPACT_CATEGORY_BADGE[r.category]}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[r.company, r.location].filter(Boolean).join(' • ') || '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleFeatured(r)}
                        title="Marcar como destacado"
                      >
                        <Star
                          className={`h-4 w-4 ${
                            r.featured ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'
                          }`}
                        />
                      </Button>
                    </TableCell>
                    <TableCell>{r.sort_order}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setDeleteId(r.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar registro' : 'Nuevo registro de impacto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Título *</Label>
              <Input
                value={form.title ?? ''}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div>
              <Label>Subtítulo</Label>
              <Input
                value={form.subtitle ?? ''}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <RichTextEditor
                value={form.description ?? ''}
                onChange={(html) => setForm({ ...form, description: html })}
                placeholder="Describe el impacto, contexto, resultados..."
              />
            </div>
            <div>
              <Label>URL de Video (YouTube)</Label>
              <Input
                value={form.video_url ?? ''}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Opcional. Acepta enlaces de YouTube (watch, youtu.be, embed o shorts).
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoría *</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v as ImpactCategory })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conferencia_taller">Conferencias y Talleres</SelectItem>
                    <SelectItem value="proyecto">Proyectos</SelectItem>
                    <SelectItem value="caso_exito">Casos de Éxito</SelectItem>
                    <SelectItem value="testimonio">Testimonios (video)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={form.date ?? ''}
                  onChange={(e) => setForm({ ...form, date: e.target.value || null })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Empresa</Label>
                <Input
                  value={form.company ?? ''}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
              </div>
              <div>
                <Label>Ubicación</Label>
                <Input
                  value={form.location ?? ''}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Ciudad, País"
                />
              </div>
            </div>
            <div>
              <Label>Imagen</Label>
              <div className="flex gap-2 items-center">
                <Input
                  value={form.image_url ?? ''}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="URL o sube una imagen"
                />
                <label>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUpload}
                  />
                  <Button asChild variant="outline" size="sm" disabled={uploading}>
                    <span className="cursor-pointer">
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                    </span>
                  </Button>
                </label>
              </div>
              {form.image_url && (
                <img
                  src={form.image_url}
                  alt=""
                  className="mt-2 max-h-40 rounded object-cover"
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  checked={form.featured ?? false}
                  onCheckedChange={(c) => setForm({ ...form, featured: c })}
                />
                <Label>Destacado (aparece en el home)</Label>
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={form.sort_order ?? 0}
                  onChange={(e) =>
                    setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={create.isPending || update.isPending}>
              {(create.isPending || update.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editing ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este registro?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteId) await remove.mutateAsync(deleteId);
                setDeleteId(null);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
